import clsx from 'clsx';
import { useState } from 'react';
import { AutoCompleteInput } from 'src/components/AutoCompleteInput';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { RadioGroup } from 'src/components/RadioGroup';
import { Textarea } from 'src/components/Textarea';
import {
  ActionPayload,
  ActionType,
  ConferenceAgentOptions,
  PhraseTriggerConditionType,
  TriggerType,
} from 'src/interfaces/action.interface';
import { triggerTypes } from 'src/stubs/action.stub';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { validateActionPhoneNumber } from 'src/utils/telephony';
import { Checkbox } from '@headlessui/react';
import { AppointmentBookingSkill } from 'src/pages/Agents/components/Skills/AppointmentBookingSkill';

interface ActionFormWarmTransferProps {
  form: ActionPayload;
  setForm: (form: ActionPayload) => void;
  loading: boolean;
  showRequiredFields?: boolean;
  phoneNumberError?: string | null;
  setPhoneNumberError?: (err: string | null) => void;
}

export const ActionFormWarmTransfer = ({
  form,
  setForm,
  loading,
  showRequiredFields,
  phoneNumberError: externalPhoneNumberError,
  setPhoneNumberError: setExternalPhoneNumberError,
}: ActionFormWarmTransferProps) => {
  const { get } = useAuthedRequest();
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);
  const [selectedTransferOption, setSelectedTransferOption] = useState<string>(
    form.config.transfer?.type || 'fluents',
  );

  const fetchLastNumbers = async (search: string) => {
    try {
      const data = await get(`/numbers/recent?number=${search}`);
      return data.map((i: { id: string; number: string }) => ({
        id: i.id,
        label: i.number,
        value: i.number,
      }));
    } catch (error) {
      console.error('Failed to fetch last numbers:', error);
      return [];
    }
  };

  // Use external error state if provided
  const error =
    externalPhoneNumberError !== undefined
      ? externalPhoneNumberError
      : phoneNumberError;
  const setError = setExternalPhoneNumberError || setPhoneNumberError;

  return (
    <div className="flex flex-col gap-6 px-2">
      {showRequiredFields && (
        <div className="flex justify-end italic text-sm mt-4">
          * Required fields
        </div>
      )}
      <div className="flex justify-between gap-6">
        <div className="w-full min-w-[300px]">
          <Input
            label="What is the action name?"
            type="text"
            id="label"
            placeholder="Enter Action Name"
            value={form.label}
            onChange={(e) =>
              setForm({
                ...form,
                label: e.target.value as string,
              })
            }
            disabled={loading}
          />
        </div>

        <div className="w-full min-w-[300px]">
          <Label htmlFor="description">How can we describe this action?</Label>
          <Textarea
            id="description"
            placeholder="Ex: this action is used to create an appointment"
            value={form.description}
            onChange={(e) =>
              setForm({
                ...form,
                description: e.target.value,
              })
            }
            disabled={loading}
            rows={4}
          />
        </div>
      </div>

      <div className="flex justify-between gap-6">
        <div className="w-full min-w-[300px]">
          <AutoCompleteInput
            label="What is the number to dial?"
            id="phone-number"
            placeholder="Enter Number to Dial"
            value={form.config.phone_number || ''}
            onChange={(value) => {
              const { error } = validateActionPhoneNumber(form.type, value);
              setError(error);
              setForm({
                ...form,
                config: {
                  ...form.config,
                  phone_number: value as string,
                },
              });
            }}
            fetchOptions={fetchLastNumbers}
            disabled={loading}
            required
          />
          {error && <div className="text-error text-xs mt-1">{error}</div>}
        </div>
      </div>

      <div className="flex justify-between gap-6">
        <div className="w-full min-w-[300px]">
          <div>On connecting with the transfer recipient...</div>
          <RadioGroup
            items={[
              {
                value: 'fluents',
                content: <p>let Fluents provide a summary</p>,
              },
              {
                value: 'user',
                content: (
                  <>
                    <p>say</p>
                    <Input
                      id="transfer-message"
                      placeholder="Enter Transfer Message"
                      value={form.config.transfer?.message || ''}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          config: Object.assign({}, form.config, {
                            transfer: {
                              type: selectedTransferOption,
                              message: e.target.value,
                            },
                          }),
                        })
                      }
                      disabled={selectedTransferOption !== 'user'}
                    />
                  </>
                ),
              },
            ]}
            defaultValue={selectedTransferOption}
            onChange={(value) => {
              setSelectedTransferOption(value);
              setForm({
                ...form,
                config: Object.assign({}, form.config, {
                  transfer: { type: value },
                }),
              });
            }}
            className="mt-2"
          />
        </div>
      </div>

      <div className="flex justify-between gap-6">
        <ConferenceActionsComponent
          form={form}
          setForm={setForm}
          conferenceAgentOptions={form.config.conference}
        />
      </div>

      <div>
        <Label required htmlFor="action-trigger">
          What should trigger this action?
        </Label>

        <div className="flex gap-4">
          {triggerTypes.map((trigger) => (
            <div
              key={trigger.value}
              className={clsx(
                'border rounded-lg cursor-pointer w-full min-w-[300px]',
                'hover:border-primary/25 hover:shadow-md',
                form.action_trigger.type === trigger.value
                  ? 'p-1 action-box-selected rounded-lg shadow-md'
                  : 'p-3 m-1 border-neutral-100 bg-neutral-0',
                form.type === ActionType.DTMF &&
                  trigger.value === TriggerType.PhraseBased &&
                  'opacity-50 cursor-not-allowed',
              )}
              onClick={() => {
                if (
                  form.type === ActionType.DTMF &&
                  trigger.value === TriggerType.PhraseBased
                ) {
                  return;
                }

                setForm({
                  ...form,
                  action_trigger: { type: trigger.value },
                });
              }}
            >
              <div
                className={clsx(
                  'bg-neutral-0',
                  form.action_trigger.type === trigger.value &&
                    'p-3 rounded-md h-full',
                )}
              >
                <div className="flex items-start mb-2 justify-between">
                  <p className="font-regular">{trigger.label}</p>
                  <div
                    className={clsx(
                      'w-[18px] h-[18px] rounded-full border b-1 border-neutral-200',
                      form.action_trigger.type === trigger.value
                        ? 'bg-primary-300'
                        : 'bg-neutral-0',
                    )}
                  ></div>
                </div>
                <p className="text-sm text-neutral-500">
                  {trigger.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {form.action_trigger.type === TriggerType.PhraseBased && (
        <div className="w-full min-w-[300px]">
          <Input
            label="What is the phrase to trigger on?"
            type="text"
            id="phrase-trigger"
            placeholder="Ex: 'Transfer me'"
            value={
              (
                form.action_trigger.config as {
                  phrase_triggers?: { phrase: string }[];
                }
              )?.phrase_triggers?.[0]?.phrase || ''
            }
            onChange={(e) =>
              setForm({
                ...form,
                action_trigger: {
                  ...form.action_trigger,
                  config: {
                    phrase_triggers: [
                      {
                        phrase: e.target.value as string,
                        conditions: [
                          PhraseTriggerConditionType.PhraseConditionTypeContains,
                        ],
                      },
                    ],
                  },
                },
              })
            }
            disabled={loading}
            required
          />
        </div>
      )}
    </div>
  );
};

const ConferenceActionsComponent = ({
  form,
  setForm,
  conferenceAgentOptions,
}: {
  form: ActionPayload;
  setForm: (form: ActionPayload) => void;
  conferenceAgentOptions?: ConferenceAgentOptions;
}) => {
  return (
    <div className="w-full min-w-[300px]">
      <div>When transfer recipient is not available...</div>
      <div className="flex flex-col gap-2 ml-2 my-2">
        <div className="flex gap-2 items-center">
          <Checkbox
            id="say_message"
            className={`
          ${conferenceAgentOptions?.say_message?.enabled ? 'bg-primary border-transparent' : 'bg-neutral-0 border-neutral-200'}
          relative inline-flex h-5 w-5 items-center justify-center rounded border-2
          transition-colors duration-200 ease-in-out
          focus:outline-none cursor-pointer
        `}
            onChange={(checked) => {
              setForm({
                ...form,
                config: {
                  ...form.config,
                  conference: {
                    ...form.config.conference,
                    say_message: {
                      enabled: checked,
                      message:
                        form.config.conference?.say_message?.message || '',
                    },
                  },
                },
              });
            }}
            checked={conferenceAgentOptions?.say_message?.enabled ?? false}
          >
            <span
              className={`
              pointer-events-none absolute h-3 w-3 transform
              transition-opacity duration-200 ease-in-out
              `}
            >
              <svg
                className="h-3 w-3 text-neutral-0"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M3 6l2 2 4-4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </Checkbox>
          <div
            className={clsx(
              'flex w-full items-center gap-2',
              !conferenceAgentOptions?.say_message?.enabled &&
                'opacity-50 cursor-not-allowed',
            )}
          >
            <p>say</p>
            <Input
              id="say_message"
              placeholder="Enter Message"
              disabled={!conferenceAgentOptions?.say_message?.enabled}
              value={conferenceAgentOptions?.say_message?.message || ''}
              onChange={(e) => {
                setForm({
                  ...form,
                  config: {
                    ...form.config,
                    conference: {
                      ...form.config.conference,
                      say_message: {
                        enabled: true,
                        message: e.target.value,
                      },
                    },
                  },
                });
              }}
            />
          </div>
        </div>
        <div>
          <div className="flex gap-2 items-center">
            <Checkbox
              id="schedule_appointment"
              className={`
          ${conferenceAgentOptions?.schedule_appointment?.enabled ? 'bg-primary border-transparent' : 'bg-neutral-0 border-neutral-200'}
          relative inline-flex h-5 w-5 items-center justify-center rounded border-2
          transition-colors duration-200 ease-in-out
          focus:outline-none cursor-pointer
        `}
              onChange={(checked) => {
                setForm({
                  ...form,
                  config: {
                    ...form.config,
                    conference: {
                      ...form.config.conference,
                      schedule_appointment: {
                        enabled: checked,
                        config:
                          form.config.conference?.schedule_appointment
                            ?.config || {},
                      },
                    },
                  },
                });
              }}
              checked={
                conferenceAgentOptions?.schedule_appointment?.enabled ?? false
              }
            >
              <span
                className={`
              pointer-events-none absolute h-3 w-3 transform
              transition-opacity duration-200 ease-in-out
              `}
              >
                <svg
                  className="h-3 w-3 text-neutral-0"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M3 6l2 2 4-4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </Checkbox>
            <p
              className={clsx(
                !conferenceAgentOptions?.schedule_appointment?.enabled &&
                  'opacity-50 cursor-not-allowed',
              )}
            >
              attempt to schedule an appointment
            </p>
          </div>
          {conferenceAgentOptions?.schedule_appointment?.enabled && (
            <AppointmentBookingSkill
              config={
                conferenceAgentOptions?.schedule_appointment?.config || {}
              }
              onConfigChange={(calendarConfig) => {
                setForm({
                  ...form,
                  config: {
                    ...form.config,
                    conference: {
                      ...form.config.conference,
                      schedule_appointment: {
                        enabled: true,
                        config: calendarConfig,
                      },
                    },
                  },
                });
              }}
            />
          )}
        </div>
      </div>
      <div>then end the call gracefully</div>
    </div>
  );
};
